import { useTranslation } from "react-i18next";
import { useRef, useEffect } from "react";
import { IF_SUB_SCHEME_ID_VAL_MAPPING, IF_SUB_SCHEMES } from "../../../config/constant";
import { BiMinusCircle, BiPlusCircle } from "react-icons/bi";
import InputField from "../../common/InputField";

const IfSubScheme = ({ scheme, onChange, errors }) => {
    const subSchemes = Array.isArray(scheme?.sub_schemes) ? scheme.sub_schemes : [];
    const { t } = useTranslation("common");
    const radioRefs = useRef({});

    useEffect(() => {
        subSchemes.forEach((ss) => {
            const ref = radioRefs.current[ss.sub_scheme_type_id];
            if (ref) {
                ref.checked = ss.first_reduction;
            }
        });
    }, [scheme?.id, scheme?.temp_id]);

    const addSubSchemeRow = (typeId) => {
        const updatedSubSchemes = scheme?.sub_schemes?.map((ss) => {
            if (ss.sub_scheme_type_id !== typeId) return ss;

            return {
                ...ss,
                sub_scheme_details: [
                    ...ss.sub_scheme_details,
                    {
                        min_value: "",
                        max_value: "",
                        incentive_percent: "",
                        status: 1,
                    },
                ],
            };
        });
        onChange("sub_schemes", updatedSubSchemes, "", scheme);
    };

    const removeSubSchemeRow = (typeId, index) => {
        const updatedSubSchemes = scheme?.sub_schemes?.map((ss) => {
            if (ss.sub_scheme_type_id !== typeId) return ss;

            return {
                ...ss,
                sub_scheme_details: ss.sub_scheme_details.filter((_, i) => i !== index),
            };
        });

        onChange("sub_schemes", updatedSubSchemes, "", scheme);
    };

    const handleSubSchemeDetailChange = (e, typeId, rowIndex) => {
        const { name, value } = e.target;

        const nameParts = name.split("_");
        nameParts.pop();
        nameParts.pop();
        const fieldName = nameParts.join("_");

        const updatedSubSchemes = subSchemes.map((ss) => {
            if (ss.sub_scheme_type_id !== typeId) return ss;

            return {
                ...ss,
                sub_scheme_details: ss.sub_scheme_details.map((row, index) => {
                    if (index !== rowIndex) return row;

                    return {
                        ...row,
                        [fieldName]: value,
                    };
                }),
            };
        });

        onChange("sub_schemes", updatedSubSchemes, "", scheme);
    };

    const handleFirstReductionChange = () => {
        const updatedSubSchemes = subSchemes.map((ss) => {
            const isChecked = radioRefs.current[ss.sub_scheme_type_id]?.checked || false;
            return {
                ...ss,
                first_reduction: isChecked,
            };
        });

        onChange("sub_schemes", updatedSubSchemes, "", scheme);
    };

    const handleSubSchemeToggle = (subSchemeKey) => {
        const typeId = IF_SUB_SCHEMES[subSchemeKey];

        const existing = subSchemes.find((ss) => ss.sub_scheme_type_id === typeId);

        let updatedSubSchemes;

        if (existing) {
            updatedSubSchemes = subSchemes.filter((ss) => ss.sub_scheme_type_id !== typeId);
        } else {
            updatedSubSchemes = [
                ...subSchemes,
                {
                    sub_scheme_type_id: typeId,
                    first_reduction: false,
                    sub_scheme_details: [
                        {
                            min_value: "",
                            max_value: "",
                            incentive_percent: "",
                            status: 1,
                        },
                    ],
                },
            ];
        }

        onChange("sub_schemes", updatedSubSchemes, "", scheme);
    };

    return (
        <div className="if-sub-scheme-container">
            <h3 className="h3">{"Sub Schemes"}</h3>
            <div className="if-sub-schemes">
                <div className="row w-100">
                    {Object.entries(scheme?.form_config?.SUB_SCHEMES).map(([subSchemeKey, isApplicable]) => {
                        if (!+isApplicable) return null;

                        const subScheme = scheme?.sub_schemes?.find(
                            (sub_scheme) => sub_scheme.sub_scheme_type_id === IF_SUB_SCHEMES[subSchemeKey]
                        );

                        return (
                            <div className="col-md-6" key={subSchemeKey}>
                                <div className="custom-control custom-checkbox m-lg-r">
                                    <input
                                        id={subSchemeKey}
                                        type="checkbox"
                                        className="custom-control-input"
                                        name="sub_scheme"
                                        value={""}
                                        onChange={() => handleSubSchemeToggle(subSchemeKey)}
                                        checked={!!subScheme}
                                    />
                                    <label className="custom-control-label">
                                        <span>{t(`IF.FORM.SUB_SCHEME.${subSchemeKey}`)}</span>
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {subSchemes.length > 1 && (
                <div className="row w-100 m-lg-b">
                    {Object.entries(scheme?.form_config?.SUB_SCHEMES).map(([subSchemeKey, isApplicable]) => {
                        if (!+isApplicable) return null;

                        const typeId = IF_SUB_SCHEMES[subSchemeKey];

                        const subScheme = subSchemes.find((ss) => ss.sub_scheme_type_id === typeId);

                        if (!subScheme) return null;

                        return (
                            <div className="col-md-6" key={typeId + "_" + (scheme.id || scheme.temp_id)}>
                                <div className="radio-btn">
                                    <div className="custom-control custom-checkbox">
                                        <input
                                            ref={(el) => (radioRefs.current[typeId] = el)}
                                            id={`first_reduction_${typeId}_${scheme.id || scheme.temp_id}`}
                                            type="radio"
                                            className="custom-control-input"
                                            name={`first_reduction_${scheme.id || scheme.temp_id}`}
                                            onChange={handleFirstReductionChange}
                                            style={{ cursor: "pointer" }}
                                            defaultChecked={subScheme.first_reduction}
                                        />
                                        <label
                                            htmlFor={`first_reduction_${typeId}_${scheme.id || scheme.temp_id}`}
                                            className="custom-control-label"
                                        >
                                            <span>First Reduction</span>
                                        </label>
                                    </div>
                                    {errors && errors[`first_reduction_${typeId}_${scheme.id || scheme.temp_id}`] ? (
                                        <span className="error show">
                                            {errors[`first_reduction_${typeId}_${scheme.id || scheme.temp_id}`]}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {subSchemes.map((sub_scheme) => {
                const typeId = sub_scheme.sub_scheme_type_id;
                const subScheme = scheme?.sub_schemes?.find((ss) => ss?.sub_scheme_type_id === typeId);
                if (!subScheme) return null;

                return (
                    <div key={typeId} className="extra-incentive-outer add-incentive-table m-b-0">
                        <h3 className="h3">
                            {subScheme?.sub_scheme_type?.name ||
                                IF_SUB_SCHEME_ID_VAL_MAPPING[subScheme.sub_scheme_type_id]}
                        </h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Min</th>
                                    <th>Max</th>
                                    <th>Incentive %</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {subScheme?.sub_scheme_details?.map((data, index) => (
                                    <tr key={data.id || index}>
                                        <td>
                                            <fieldset className="m-sm-r">
                                                <div className="material">
                                                    <InputField
                                                        inputProps={{
                                                            id: `min_value_${typeId}_${index}`,
                                                            type: "text",
                                                            name: `min_value_${typeId}_${index}`,
                                                            value: data.min_value || "",
                                                            className: "form-input",
                                                        }}
                                                        onChange={(e) => handleSubSchemeDetailChange(e, typeId, index)}
                                                    />
                                                </div>
                                                {errors && errors[`min_value_${typeId}_${index}`] ? (
                                                    <span className="error show">
                                                        {errors[`min_value_${typeId}_${index}`]}
                                                    </span>
                                                ) : (
                                                    <span>&nbsp;</span>
                                                )}
                                            </fieldset>
                                        </td>
                                        <td>
                                            <fieldset className="m-sm-r">
                                                <div className="material">
                                                    <InputField
                                                        inputProps={{
                                                            id: `max_value_${typeId}_${index}`,
                                                            type: "text",
                                                            name: `max_value_${typeId}_${index}`,
                                                            value: data.max_value || "",
                                                            className: "form-input",
                                                        }}
                                                        onChange={(e) => handleSubSchemeDetailChange(e, typeId, index)}
                                                    />
                                                </div>
                                                {errors && errors[`max_value_${typeId}_${index}`] ? (
                                                    <span className="error show">
                                                        {errors[`max_value_${typeId}_${index}`]}
                                                    </span>
                                                ) : (
                                                    <span>&nbsp;</span>
                                                )}
                                            </fieldset>
                                        </td>

                                        <td>
                                            <fieldset>
                                                <div className="material m-sm-r">
                                                    <InputField
                                                        inputProps={{
                                                            id: `incentive_percent_${typeId}_${index}`,
                                                            type: "text",
                                                            name: `incentive_percent_${typeId}_${index}`,
                                                            value: data.incentive_percent,
                                                            className: "form-input",
                                                        }}
                                                        onChange={(e) => handleSubSchemeDetailChange(e, typeId, index)}
                                                    />
                                                </div>
                                                {errors && errors[`incentive_percent_${typeId}_${index}`] ? (
                                                    <span className="error show">
                                                        {errors[`incentive_percent_${typeId}_${index}`]}
                                                    </span>
                                                ) : (
                                                    <span>&nbsp;</span>
                                                )}
                                            </fieldset>
                                        </td>
                                        <td>
                                            {index === 0 ? (
                                                <span className="add-minus" onClick={() => addSubSchemeRow(typeId)}>
                                                    <BiPlusCircle size="1.4em" />
                                                </span>
                                            ) : (
                                                <span
                                                    className="add-minus"
                                                    onClick={() => removeSubSchemeRow(typeId, index)}
                                                >
                                                    <BiMinusCircle size="1.4em" />
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
};

export default IfSubScheme;
